import React, { useContext, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import logoSmall from "../assets/logo.svg";
import { getDefaultHelpSectionId, resolveHelpPersona } from "../help/personas";

const NAV_ITEMS = {
  home: { id: "home", labelKey: "nav.home", path: "/" },
  certify: { id: "certify", labelKey: "nav.certify", path: "/cert" },
  mint: { id: "mint", labelKey: "nav.mint", path: "/mint" },
  mynfts: { id: "mynfts", labelKey: "nav.myNfts", path: "/owner" },
  verify: { id: "verify", labelKey: "nav.verify", path: "/verify" },
  transfer: {
    id: "transfer",
    labelKey: "nav.transfer",
    path: "/reassign",
  },
  users: { id: "users", labelKey: "nav.users", path: "/admin" },
  partners: { id: "partners", labelKey: "nav.partners", path: "/partners" },
  help: { id: "help", labelKey: "nav.help", path: "/help" },
};

function buildNavigation({ isPlatformAdmin, canCertify, hasCompanyRole, canManageCompanyUsers, companyType }) {
  const items = [NAV_ITEMS.home, NAV_ITEMS.mynfts];
  if (companyType !== "CERTIFICATION_ENTITY") {
    items.push(NAV_ITEMS.mint);
  }

  if (isPlatformAdmin) {
    items.push(
      NAV_ITEMS.certify,
      NAV_ITEMS.verify,
      NAV_ITEMS.transfer,
      NAV_ITEMS.users,
      NAV_ITEMS.partners,
      NAV_ITEMS.help
    );
    return items;
  }

  if (!hasCompanyRole) {
    items.push(NAV_ITEMS.verify, NAV_ITEMS.transfer, NAV_ITEMS.help);
    return items;
  }

  if (canCertify) {
    items.push(NAV_ITEMS.certify);
  }
  items.push(NAV_ITEMS.verify, NAV_ITEMS.transfer);
  if (!isPlatformAdmin && canManageCompanyUsers) {
    items.push(NAV_ITEMS.users);
  }
  items.push(NAV_ITEMS.help);
  return items;
}

export function Navbar() {
  const { token, logout, company, companyRole, isPlatformAdmin, canCertify, canManageCompanyUsers } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  const { t, i18n } = useTranslation();
  const showLanguageSelect = currentPath === "/login" || currentPath === "/partners/apply";
  const companyType = String(company?.company_type || "").toUpperCase();
  const isBusinessInventoryUser = ["CERTIFICATION_ENTITY", "INTERMEDIARY", "PLATFORM"].includes(companyType);
  const hidePublicAuthLinks = currentPath === "/partners/apply";
  const helpPersona = useMemo(
    () =>
      resolveHelpPersona({
        isPlatformAdmin,
        canManageCompanyUsers,
        canCertify,
        companyRole,
      }),
    [isPlatformAdmin, canManageCompanyUsers, canCertify, companyRole]
  );
  const helpDefaultSectionId = useMemo(
    () => getDefaultHelpSectionId(helpPersona),
    [helpPersona]
  );

  const optionsForRole = useMemo(
    () =>
      buildNavigation({
        isPlatformAdmin,
        canCertify,
        hasCompanyRole: Boolean(companyRole),
        canManageCompanyUsers,
        companyType: company?.company_type,
      }),
    [isPlatformAdmin, canCertify, companyRole, canManageCompanyUsers, company?.company_type]
  );

  const handleNavigationChange = (event) => {
    const nextPath = event.target.value;
    if (!nextPath) {
      return;
    }

    if (nextPath === "logout") {
      logout();
      navigate("/login");
      return;
    }

    if (nextPath === NAV_ITEMS.help.path) {
      navigate(`/help#${helpDefaultSectionId}`);
      return;
    }

    if (nextPath !== currentPath) {
      navigate(nextPath);
    }
  };

  const selectValue = optionsForRole.find((option) => option.path === currentPath)
    ?.path || "";

  const handleLanguageChange = (event) => {
    const nextLang = event.target.value;
    if (nextLang && nextLang !== i18n.language) {
      i18n.changeLanguage(nextLang);
    }
  };

  return (
    <header className="app-header">
      <Link to="/" className="app-header__brand">
        <img
          alt={t("nav.logoAlt")}
          className="app-header__logo"
          src={logoSmall}
        />
        <span className="app-header__brand-text">{t("app.title") + "\u2122"}</span>
      </Link>
      <div className="app-header__actions">
        {showLanguageSelect && (
          <select
            aria-label={t("nav.languageLabel")}
            className="app-header__select app-header__select--lang"
            onChange={handleLanguageChange}
            value={i18n.language}
          >
            <option value="en-US">{t("nav.language.en")}</option>
            <option value="es-AR">{t("nav.language.es")}</option>
          </select>
        )}
        {token ? (
          <select
            aria-label={t("nav.ariaNavigate")}
            className="app-header__select"
            onChange={handleNavigationChange}
            value={selectValue}
          >
            <option value="" disabled>
              {t("nav.placeholder")}
            </option>
            {optionsForRole.map((option) => (
              <option key={option.id} value={option.path}>
                {t(
                  option.id === "mynfts" && isBusinessInventoryUser
                    ? "nav.inventory"
                    : option.labelKey
                )}
              </option>
            ))}
            <option value="logout">{t("nav.logout")}</option>
          </select>
        ) : (
          !hidePublicAuthLinks && (
            <div className="app-header__auth-links">
              {currentPath !== "/login" && currentPath !== "/register" && (
                <Link className="app-header__link" to="/login">
                  {t("nav.login")}
                </Link>
              )}
              {currentPath !== "/register" && currentPath !== "/login" && (
                <Link className="app-header__link" to="/register">
                  {t("nav.register")}
                </Link>
              )}
            </div>
          )
        )}
      </div>
    </header>
  );
}
